<!-- Responsibility: Implement sys panel behavior for the ui/panels surface.
Scope: Owns ui/panels concerns only; cross-surface orchestration is excluded. -->
<sys-panel>
  <div class="panel-content">
    <h5 class="section-title">{ state.devOnly ? 'DEV OBSERVABILITY HUB' : 'SYSTEM CONTROLS' }</h5>

    <p if={ state.devOnly } class="perf-note" style="margin-bottom:var(--space-md);">
      Centralized diagnostics view for AI runtime behavior. This hub groups throughput, latency, token/cost estimates, feedback pipeline health, reporting queue state, and offline sync counters in one place.
    </p>

    <sys-section-account
      view={ accountSectionView() }
      actions={ accountSectionActions() }
    ></sys-section-account>

    <sys-section-ai-performance
      view={ aiPerformanceSectionView() }
      actions={ aiPerformanceSectionActions() }
    ></sys-section-ai-performance>

    <sys-section-supervisor
      if={ state.devOnly }
      view={ supervisorSectionView() }
      actions={ supervisorSectionActions() }
    ></sys-section-supervisor>

    <sys-section-feedback-reporting
      view={ feedbackSectionView() }
      actions={ feedbackSectionActions() }
    ></sys-section-feedback-reporting>

    <sys-section-help-display
      view={ helpDisplaySectionView() }
      actions={ helpDisplaySectionActions() }
    ></sys-section-help-display>
  </div>

  <sys-gallery-modal openSeq={ state.galleryOpenSeq }></sys-gallery-modal>

  <script>
    import { dispatcher } from '../../app-states.js'
    import { TOPICS } from "@anyquest/shared-core"
    import { authStore } from '../stores/auth-store.js'
    import * as gameStore from '../../game-store.js'
    import {
      isDriveSyncConfigured,
      isDriveConnected,
      connectDrive,
      listDriveBackups,
      uploadDriveBackup,
      downloadDriveBackup,
    } from '../../engine/gdrive-sync.js'
    import { router as aiRouter } from '../../ai/router.js'
    import {
      getAITelemetry,
      summarizeAITelemetry,
      clearAITelemetry,
      onAITelemetryChange,
      buildAITelemetryTrend,
      buildAITelemetrySnapshot,
      getAICostRateCard,
      setAICostRateCard,
    } from '../../ai/telemetry.js'
    import {
      runSupervisorPass,
      getSupervisorRuns,
      getSupervisorIncidents,
      clearSupervisorAuditLog,
      remediateIncidentById,
      rollbackSupervisorState,
      getSupervisorRollbackSnapshots,
    } from '../../engine/supervisor/supervisor-engine.js'
    import {
      getSupervisorConfig,
      setSupervisorConfig,
      DEFAULT_SUPERVISOR_CONFIG,
    } from '../../engine/supervisor/policy.js'
    import SysSectionAccount from './sys-section-account.riot'
    import SysSectionAiPerformance from './sys-section-ai-performance.riot'
    import SysSectionSupervisor from './sys-section-supervisor.riot'
    import SysSectionFeedbackReporting from './sys-section-feedback-reporting.riot'
    import SysSectionHelpDisplay from './sys-section-help-display.riot'
    import SysGalleryModal from './sys-gallery-modal.riot'
    import { playEffect } from '../../engine/audio.js'
    import {
      REPORT_ENDPOINT_KEY,
      loadReportQueue,
      saveReportQueue,
    } from './sys-panel-storage.js'
    import { emailService } from '../../services/email-service.ts'
    import { persistenceService } from '../../services/persistence-service.ts'

    const DIM_KEY = 'am2_dim'
    const SUPERVISOR_DOMAINS = ['actor', 'inventory', 'atlas', 'combat', 'quest', 'effect', 'persistence', 'object']
    export default {
      components: { SysSectionAccount, SysSectionAiPerformance, SysSectionSupervisor, SysSectionFeedbackReporting, SysSectionHelpDisplay, SysGalleryModal },
      state: {
        devOnly: false,
        isGuest:       true,
        userName:      '',
        aiStatus:      'Checking providers...',
        isDim:         false,
        galleryOpenSeq: 0,
        perfSummary:   [],
        perfCount:     0,
        perfWindow:    '24h',
        perfTrend:     [],
        perfTrendMax:  1,
        perfInputTokens: 0,
        perfOutputTokens: 0,
        perfCostUsd:   0,
        perfMessage:   '',
        costRateDraft: {},
        feedbackRating: null, // 'up' | 'down' | null
        feedbackText:  '',
        feedbackSavedMsg: '',
        reportEndpoint: '',
        reportQueueCount: 0,
        reportMsg: '',
        reportBusy: false,
        persistMode: 'hybrid',
        persistBusy: false,
        persistMsg: '',
        backupBusy: false,
        backupMsg: '',
        driveAvailable: false,
        driveConnected: false,
        driveBusy: false,
        driveMsg: '',
        driveFiles: [],
        pendingSaveCount: 0,
        conflictBackupCount: 0,
        syncBusy: false,
        syncMsg: '',
        supervisorRuns: [],
        supervisorIncidents: [],
        supervisorMessage: '',
        supervisorLastDegraded: '',
        supervisorEnabledDraft: true,
        supervisorTimeBudgetDraft: 20,
        supervisorPolicyDraft: {},
        supervisorDomains: SUPERVISOR_DOMAINS,
        supervisorFilterDomain: 'all',
        supervisorFilterSeverity: 'all',
        supervisorFilterStatus: 'all',
      },

      accountSectionView() {
        return {
          devOnly: this.state.devOnly,
          isGuest: this.state.isGuest,
          userName: this.state.userName,
          persistMode: this.state.persistMode,
          persistBusy: this.state.persistBusy,
          persistMsg: this.state.persistMsg,
          backupBusy: this.state.backupBusy,
          backupMsg: this.state.backupMsg,
          driveAvailable: this.state.driveAvailable,
          driveConnected: this.state.driveConnected,
          driveBusy: this.state.driveBusy,
          driveMsg: this.state.driveMsg,
          driveFiles: this.state.driveFiles,
          aiStatus: this.state.aiStatus,
          formatTime: this.formatTime,
          formatBytes: this.formatBytes,
        }
      },

      accountSectionActions() {
        return {
          signOut: () => this.signOut(),
          switchPersistenceMode: (mode) => this.switchPersistenceMode(mode),
          exportActiveBackup: () => this.exportActiveBackup(),
          onBackupImportFile: (event) => this.onBackupImportFile(event),
          connectDriveNow: () => this.connectDriveNow(),
          syncActiveBackupToDrive: () => this.syncActiveBackupToDrive(),
          pullLatestDriveBackup: () => this.pullLatestDriveBackup(),
          refreshDriveBackups: () => this.refreshDriveBackups(),
          importDriveBackup: (id) => this.importDriveBackup(id),
          openAiSettings: () => this.openAiSettings(),
        }
      },

      aiPerformanceSectionView() {
        return {
          perfWindow: this.state.perfWindow,
          perfCount: this.state.perfCount,
          perfInputTokens: this.state.perfInputTokens,
          perfOutputTokens: this.state.perfOutputTokens,
          perfCostUsd: this.state.perfCostUsd,
          costRateDraft: this.state.costRateDraft,
          perfTrend: this.state.perfTrend,
          perfTrendMax: this.state.perfTrendMax,
          perfSummary: this.state.perfSummary,
          perfMessage: this.state.perfMessage,
          formatUsd: this.formatUsd,
          formatTrendLabel: this.formatTrendLabel,
        }
      },

      aiPerformanceSectionActions() {
        return {
          setPerfWindow: (windowKey) => this.setPerfWindow(windowKey),
          onCostRateInput: (provider, key, event) => this.onCostRateInput(provider, key, event),
          saveCostRates: () => this.saveCostRates(),
          resetCostRates: () => this.resetCostRates(),
          exportPerfData: () => this.exportPerfData(),
          copyPerfSnapshot: () => this.copyPerfSnapshot(),
          clearPerfData: () => this.clearPerfData(),
        }
      },

      supervisorSectionView() {
        const incidents = this.state.supervisorIncidents || []
        return {
          enabled: this.state.supervisorEnabledDraft,
          timeBudgetMs: this.state.supervisorTimeBudgetDraft,
          policyDraft: this.state.supervisorPolicyDraft,
          domains: this.state.supervisorDomains,
          runs: this.state.supervisorRuns,
          incidents,
          filteredIncidents: this.getFilteredSupervisorIncidents(),
          filterDomain: this.state.supervisorFilterDomain,
          filterSeverity: this.state.supervisorFilterSeverity,
          filterStatus: this.state.supervisorFilterStatus,
          message: this.state.supervisorMessage,
          lastDegraded: this.state.supervisorLastDegraded,
          formatTime: this.formatTime,
          pendingCount: incidents.filter(i => i.remediationStatus === 'pending').length,
          appliedCount: incidents.filter(i => i.remediationStatus === 'applied').length,
        }
      },

      supervisorSectionActions() {
        return {
          onSupervisorConfigEnabledInput: (event) => this.onSupervisorConfigEnabledInput(event),
          onSupervisorConfigBudgetInput: (event) => this.onSupervisorConfigBudgetInput(event),
          onSupervisorDomainPolicyInput: (domain, event) => this.onSupervisorDomainPolicyInput(domain, event),
          saveSupervisorConfig: () => this.saveSupervisorConfig(),
          setSupervisorFilterDomain: (value) => this.setSupervisorFilterDomain(value),
          setSupervisorFilterSeverity: (value) => this.setSupervisorFilterSeverity(value),
          setSupervisorFilterStatus: (value) => this.setSupervisorFilterStatus(value),
          runSupervisorNow: () => this.runSupervisorNow(),
          remediateSupervisorIncident: (id) => this.remediateSupervisorIncident(id),
          rollbackSupervisorSnapshot: (snapshotId) => this.rollbackSupervisorSnapshot(snapshotId),
          clearSupervisorAudit: () => this.clearSupervisorAudit(),
          exportSupervisorAudit: () => this.exportSupervisorAudit(),
        }
      },

      feedbackSectionView() {
        return {
          feedbackRating: this.state.feedbackRating,
          feedbackText: this.state.feedbackText,
          feedbackSavedMsg: this.state.feedbackSavedMsg,
          reportEndpoint: this.state.reportEndpoint,
          reportBusy: this.state.reportBusy,
          reportQueueCount: this.state.reportQueueCount,
          reportMsg: this.state.reportMsg,
          isGuest: this.state.isGuest,
          pendingSaveCount: this.state.pendingSaveCount,
          conflictBackupCount: this.state.conflictBackupCount,
          syncBusy: this.state.syncBusy,
          syncMsg: this.state.syncMsg,
          formatTime: this.formatTime,
        }
      },

      feedbackSectionActions() {
        return {
          selectFeedback: (rating) => this.selectFeedback(rating),
          onFeedbackInput: (event) => this.onFeedbackInput(event),
          submitFeedback: () => this.submitFeedback(),
          onReportEndpointInput: (event) => this.onReportEndpointInput(event),
          saveReportEndpoint: () => this.saveReportEndpoint(),
          sendPerfReport: () => this.sendPerfReport(),
          retryQueuedReports: () => this.retryQueuedReports(),
          flushPendingSaves: () => this.flushPendingSaves(),
          clearPendingQueue: () => this.clearPendingQueue(),
          clearBackups: () => this.clearBackups(),
        }
      },

      helpDisplaySectionView() {
        return {
          devOnly: this.state.devOnly,
          isDim: this.state.isDim,
        }
      },

      helpDisplaySectionActions() {
        return {
          openHowToPlay: () => this.openHowToPlay(),
          toggleDim: () => this.toggleDim(),
          openGallery: () => this.triggerGalleryOpen(),
        }
      },

      async onMounted() {
        this.update({ devOnly: this.props.mode === 'dev' })
        const user = authStore.user
        this.update({
          isGuest:  !user || user.isAnonymous,
          userName: user?.displayName ?? user?.email ?? '',
        })

        aiRouter.getStatus().then(status => {
          const lines = Object.entries(status).map(([id, ok]) => `${ok ? '●' : '○'} ${id.toUpperCase()}`)
          this.update({ aiStatus: lines.join('  ') })
        }).catch(() => this.update({ aiStatus: 'Status check failed.' }))

        // Restore dim preference
        const isDim = localStorage.getItem(DIM_KEY) === '1'
        this.update({ isDim })
        document.documentElement.classList.toggle('dim', isDim)
        this.update({ costRateDraft: getAICostRateCard() })
        this.update({ reportEndpoint: localStorage.getItem(REPORT_ENDPOINT_KEY) || '' })
          const mode = await persistenceService.getMode()
          this.update({ persistMode: mode })
        this.update({
          driveAvailable: isDriveSyncConfigured(),
          driveConnected: isDriveConnected(),
        })

        this.refreshPerfData()
        this.refreshReportQueueCount()
        this.refreshSyncCounts()
        if (isDriveSyncConfigured() && isDriveConnected()) this.refreshDriveBackups(true).catch(() => {})
        this.perfUnsub = onAITelemetryChange(() => this.refreshPerfData())

        this.refreshSupervisorData()
        this.supervisorStartHandle = dispatcher.on(TOPICS.supervisor.runStarted, (_e, data) => {
          this.update({ supervisorMessage: `Supervisor run started (${data?.reason || 'unknown'})...` })
        })
        this.supervisorEndHandle = dispatcher.on(TOPICS.supervisor.runEnded, (_e, data) => {
          this.refreshSupervisorData()
          this.update({
            supervisorMessage: `Supervisor run finished: ${Math.round(data?.durationMs || 0)}ms, incidents ${data?.incidents || 0}, repaired ${data?.repaired || 0}.`,
          })
        })
        this.supervisorDegradedHandle = dispatcher.on(TOPICS.supervisor.degraded, (_e, data) => {
          const reason = String(data?.reason || 'unknown')
          this.update({ supervisorLastDegraded: reason, supervisorMessage: `Supervisor degraded: ${reason}` })
        })
      },

      onUnmounted() {
        this.perfUnsub?.()
        if (this.supervisorStartHandle) dispatcher.off(this.supervisorStartHandle)
        if (this.supervisorEndHandle) dispatcher.off(this.supervisorEndHandle)
        if (this.supervisorDegradedHandle) dispatcher.off(this.supervisorDegradedHandle)
      },

      toggleDim() {
        const isDim = !this.state.isDim
        this.update({ isDim })
        document.documentElement.classList.toggle('dim', isDim)
        localStorage.setItem(DIM_KEY, isDim ? '1' : '0')
      },

      formatUsd(v) {
        return Number(v || 0).toFixed(4)
      },

      formatTime(ts) {
        const d = new Date(ts)
        return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`
      },

      formatBytes(bytes) {
        const n = Number(bytes || 0)
        if (!Number.isFinite(n) || n <= 0) return '0 B'
        if (n < 1024) return `${n} B`
        if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
        return `${(n / (1024 * 1024)).toFixed(1)} MB`
      },

      formatTrendLabel(ts) {
        const d = new Date(ts)
        if (this.state.perfWindow === '1h' || this.state.perfWindow === '6h') {
          return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        }
        return d.toLocaleDateString([], { month: 'short', day: 'numeric' })
      },

      setPerfWindow(windowKey) {
        this.update({ perfWindow: windowKey, perfMessage: '' })
        this.refreshPerfData()
      },

      windowMs() {
        if (this.state.perfWindow === '1h') return 60 * 60 * 1000
        if (this.state.perfWindow === '6h') return 6 * 60 * 60 * 1000
        if (this.state.perfWindow === '24h') return 24 * 60 * 60 * 1000
        return null
      },

      filteredPerfEntries(entries) {
        const windowMs = this.windowMs()
        if (!windowMs) return entries
        const cutoff = Date.now() - windowMs
        return entries.filter(e => e.ts >= cutoff)
      },

      refreshPerfData() {
        const allEntries = getAITelemetry()
        const entries = this.filteredPerfEntries(allEntries)
        const summary = summarizeAITelemetry(entries)
        const snapshot = buildAITelemetrySnapshot(entries)
        const trend = buildAITelemetryTrend(entries, 10)
        this.update({
          perfCount: entries.length,
          perfSummary: summary,
          perfTrend: trend,
          perfTrendMax: Math.max(1, ...trend.map(p => p.total)),
          perfInputTokens: snapshot.totals.inputTokens,
          perfOutputTokens: snapshot.totals.outputTokens,
          perfCostUsd: snapshot.totals.estCostUsd,
        })
      },

      exportPerfData() {
        const entries = this.filteredPerfEntries(getAITelemetry())
        const snapshot = buildAITelemetrySnapshot(entries)
        const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `am2-ai-perf-${Date.now()}.json`
        link.click()
        URL.revokeObjectURL(link.href)
        this.update({ perfMessage: 'Performance snapshot exported.' })
      },

      async copyPerfSnapshot() {
        const entries = this.filteredPerfEntries(getAITelemetry())
        const snapshot = buildAITelemetrySnapshot(entries)
        const text = [
          `AI PERF (${this.state.perfWindow.toUpperCase()})`,
          `Calls: ${snapshot.totals.totalCalls} (ok ${snapshot.totals.successCalls} / fail ${snapshot.totals.failCalls})`,
          `Input tokens: ${snapshot.totals.inputTokens}`,
          `Output tokens: ${snapshot.totals.outputTokens}`,
          `Estimated cost: $${this.formatUsd(snapshot.totals.estCostUsd)}`,
        ].join('\n')
        try {
          await navigator.clipboard.writeText(text)
          this.update({ perfMessage: 'Performance snapshot copied.' })
        } catch {
          this.update({ perfMessage: 'Copy failed. Browser denied clipboard access.' })
        }
      },

      clearPerfData() {
        clearAITelemetry()
        this.update({ perfMessage: 'Performance data cleared.' })
      },

      refreshSupervisorData() {
        const config = getSupervisorConfig()
        this.update({
          supervisorRuns: getSupervisorRuns().slice().reverse(),
          supervisorIncidents: getSupervisorIncidents().slice().reverse(),
          supervisorEnabledDraft: !!config.enabled,
          supervisorTimeBudgetDraft: Number(config.timeBudgetMs || 20),
          supervisorPolicyDraft: { ...DEFAULT_SUPERVISOR_CONFIG.policies, ...(config.policies || {}) },
        })
      },

      onSupervisorConfigEnabledInput(e) {
        this.update({ supervisorEnabledDraft: e?.target?.value === '1' })
      },

      onSupervisorConfigBudgetInput(e) {
        const value = Number(e?.target?.value)
        this.update({ supervisorTimeBudgetDraft: Number.isFinite(value) ? Math.max(5, Math.min(1000, value)) : 20 })
      },

      onSupervisorDomainPolicyInput(domain, e) {
        const value = String(e?.target?.value || 'observe')
        this.update({
          supervisorPolicyDraft: {
            ...this.state.supervisorPolicyDraft,
            [domain]: value,
          },
        })
      },

      saveSupervisorConfig() {
        const nextPolicies = {}
        for (const domain of SUPERVISOR_DOMAINS) {
          nextPolicies[domain] = this.state.supervisorPolicyDraft?.[domain] || 'observe'
        }
        setSupervisorConfig({
          enabled: !!this.state.supervisorEnabledDraft,
          timeBudgetMs: Number(this.state.supervisorTimeBudgetDraft || 20),
          policies: nextPolicies,
        })
        this.refreshSupervisorData()
        this.update({ supervisorMessage: 'Supervisor configuration saved.' })
      },

      setSupervisorFilterDomain(value) {
        this.update({ supervisorFilterDomain: value || 'all' })
      },

      setSupervisorFilterSeverity(value) {
        this.update({ supervisorFilterSeverity: value || 'all' })
      },

      setSupervisorFilterStatus(value) {
        this.update({ supervisorFilterStatus: value || 'all' })
      },

      getFilteredSupervisorIncidents() {
        const list = this.state.supervisorIncidents || []
        return list.filter(incident => {
          if (this.state.supervisorFilterDomain !== 'all' && incident.domain !== this.state.supervisorFilterDomain) return false
          if (this.state.supervisorFilterSeverity !== 'all' && incident.severity !== this.state.supervisorFilterSeverity) return false
          if (this.state.supervisorFilterStatus !== 'all' && incident.remediationStatus !== this.state.supervisorFilterStatus) return false
          return true
        })
      },

      async runSupervisorNow() {
        if (!gameStore.state) {
          this.update({ supervisorMessage: 'No active game state. Start/load a game first.' })
          return
        }
        this.update({ supervisorMessage: 'Running supervisor pass...' })
        try {
          const summary = await runSupervisorPass(gameStore.state, 'manual_dev')
          this.refreshSupervisorData()
          this.update({
            supervisorMessage: `Manual run complete: ${Math.round(summary.durationMs)}ms, incidents ${summary.incidentsFound}, repaired ${summary.incidentsRepaired}.`,
          })
        } catch (e) {
          this.update({ supervisorMessage: `Manual run failed: ${String(e).slice(0, 160)}` })
        }
      },

      remediateSupervisorIncident(incidentId) {
        if (!gameStore.state) {
          this.update({ supervisorMessage: 'No active state for remediation.' })
          return
        }
        const result = remediateIncidentById(gameStore.state, incidentId)
        if (result.applied) {
          dispatcher.trigger('sys-panel', TOPICS.game.actionUpdate, { state: gameStore.state })
          dispatcher.trigger('sys-panel', TOPICS.game.inventoryChanged)
          dispatcher.trigger('sys-panel', TOPICS.game.vitalsChanged, gameStore.state.player.vitals)
        }
        this.refreshSupervisorData()
        this.update({ supervisorMessage: result.message })
      },

      rollbackSupervisorSnapshot(snapshotId) {
        if (!gameStore.state) {
          this.update({ supervisorMessage: 'No active state for rollback.' })
          return
        }
        const ok = rollbackSupervisorState(gameStore.state, snapshotId)
        if (ok) {
          dispatcher.trigger('sys-panel', TOPICS.game.actionUpdate, { state: gameStore.state })
          dispatcher.trigger('sys-panel', TOPICS.game.inventoryChanged)
          dispatcher.trigger('sys-panel', TOPICS.game.vitalsChanged, gameStore.state.player.vitals)
          this.update({ supervisorMessage: 'Rollback applied from snapshot.' })
        } else {
          this.update({ supervisorMessage: 'Rollback snapshot not found.' })
        }
        this.refreshSupervisorData()
      },

      clearSupervisorAudit() {
        clearSupervisorAuditLog()
        this.refreshSupervisorData()
        this.update({ supervisorMessage: 'Supervisor audit log cleared.' })
      },

      exportSupervisorAudit() {
        const payload = {
          generatedAt: Date.now(),
          config: getSupervisorConfig(),
          runs: getSupervisorRuns(),
          incidents: getSupervisorIncidents(),
          rollbackSnapshots: getSupervisorRollbackSnapshots(),
        }
        const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `am2-supervisor-audit-${Date.now()}.json`
        link.click()
        URL.revokeObjectURL(link.href)
        this.update({ supervisorMessage: 'Supervisor audit exported.' })
      },

      onCostRateInput(provider, field, e) {
        const raw = e?.target?.value ?? ''
        const value = raw === '' ? undefined : Number(raw)
        const next = {
          ...this.state.costRateDraft,
          [provider]: {
            ...(this.state.costRateDraft[provider] ?? {}),
            [field]: Number.isFinite(value) ? Math.max(0, value) : undefined,
          },
        }
        this.update({ costRateDraft: next, perfMessage: '' })
      },

      saveCostRates() {
        setAICostRateCard(this.state.costRateDraft || {})
        this.refreshPerfData()
        this.update({ perfMessage: 'Cost rates saved.' })
      },

      resetCostRates() {
        setAICostRateCard({})
        this.update({ costRateDraft: getAICostRateCard() })
        this.refreshPerfData()
        this.update({ perfMessage: 'Cost rates reset to defaults.' })
      },

      refreshReportQueueCount() {
        this.update({ reportQueueCount: loadReportQueue().length })
      },

      async refreshSyncCounts() {
        const uid = authStore.user?.uid
        if (!uid) return
        const counts = await persistenceService.getSyncCounts(uid)
        this.update({
          pendingSaveCount: counts.pendingSaveCount,
          conflictBackupCount: counts.conflictBackupCount
        })
      },

      async switchPersistenceMode(mode) {
        const normalized = mode === 'local-only' ? 'local-only' : 'hybrid'
        const uid = authStore.user?.uid
        this.update({ persistBusy: true, persistMsg: '' })
        try {
          const mode = await persistenceService.setMode(normalized)
          if (uid) {
            if (normalized === 'local-only') await persistenceService.stopUserSync(uid)
            else await persistenceService.startUserSync(uid)
          }
          this.update({
            persistMode: mode,
            persistMsg: normalized === 'local-only'
              ? 'Persistence switched to LOCAL ONLY.'
              : 'Persistence switched to HYBRID.',
          })
          await this.refreshSyncCounts()
        } catch {
          this.update({ persistMsg: 'Failed to switch persistence mode.' })
        } finally {
          this.update({ persistBusy: false })
        }
      },

      async exportActiveBackup() {
        this.update({ backupBusy: true, backupMsg: '' })
        try {
          const raw = await persistenceService.exportActiveBackup()
          const blob = new Blob([raw], { type: 'application/json' })
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = `am2-save-backup-${Date.now()}.json`
          link.click()
          URL.revokeObjectURL(link.href)
          this.update({ backupMsg: 'Backup exported.' })
        } catch {
          this.update({ backupMsg: 'Export failed (no active save or session).' })
        } finally {
          this.update({ backupBusy: false })
        }
      },

      async connectDriveNow() {
        this.update({ driveBusy: true, driveMsg: '' })
        try {
          await connectDrive()
          this.update({ driveConnected: isDriveConnected(), driveMsg: 'Drive connected.' })
          await this.refreshDriveBackups(true)
        } catch (e) {
          this.update({ driveMsg: `Drive connect failed: ${String(e).slice(0, 140)}` })
        } finally {
          this.update({ driveBusy: false })
        }
      },

      async refreshDriveBackups(silent = false) {
        if (!isDriveSyncConfigured()) return
        if (!silent) this.update({ driveBusy: true, driveMsg: '' })
        try {
          if (!isDriveConnected()) {
            if (silent) return
            await connectDrive()
          }
          const files = await listDriveBackups(8)
          this.update({
            driveFiles: files,
            driveConnected: isDriveConnected(),
          })
          if (!silent) {
            this.update({ driveMsg: files.length ? 'Drive backups refreshed.' : 'No Drive backups found yet.' })
          }
        } catch (e) {
          if (!silent) this.update({ driveMsg: `Drive list failed: ${String(e).slice(0, 140)}` })
        } finally {
          if (!silent) this.update({ driveBusy: false })
        }
      },

      async syncActiveBackupToDrive() {
        this.update({ driveBusy: true, driveMsg: '' })
        try {
          if (!isDriveConnected()) await connectDrive()
          const raw = await persistenceService.exportActiveBackup()
          const saveId = gameStore.activeSaveId || `active-${Date.now()}`
          await uploadDriveBackup(saveId, raw)
          this.update({ driveConnected: isDriveConnected(), driveMsg: `Backup synced to Drive (${saveId}).` })
          await this.refreshDriveBackups(true)
        } catch (e) {
          this.update({ driveMsg: `Drive sync failed: ${String(e).slice(0, 140)}` })
        } finally {
          this.update({ driveBusy: false })
        }
      },

      async pullLatestDriveBackup() {
        const latest = (this.state.driveFiles || [])[0]
        if (!latest?.id) {
          this.update({ driveMsg: 'No Drive backups available.' })
          return
        }
        await this.importDriveBackup(latest.id)
      },

      async importDriveBackup(fileId) {
        this.update({ driveBusy: true, driveMsg: '' })
        try {
          if (!isDriveConnected()) await connectDrive()
          const raw = await downloadDriveBackup(fileId)
          const saveId = await persistenceService.importBackup(raw)
          this.update({ driveConnected: isDriveConnected(), driveMsg: `Drive backup imported as save ${saveId}.` })
          await this.refreshSyncCounts()
          await this.refreshDriveBackups(true)
        } catch (e) {
          this.update({ driveMsg: `Drive import failed: ${String(e).slice(0, 140)}` })
        } finally {
          this.update({ driveBusy: false })
        }
      },

      async onBackupImportFile(e) {
        const file = e?.target?.files?.[0]
        if (!file) return
        this.update({ backupBusy: true, backupMsg: '' })
        try {
          const raw = await file.text()
          const saveId = await persistenceService.importBackup(raw)
          this.update({ backupMsg: `Backup imported as save ${saveId}.` })
          await this.refreshSyncCounts()
        } catch {
          this.update({ backupMsg: 'Import failed (invalid file or save payload).' })
        } finally {
          if (e?.target) e.target.value = ''
          this.update({ backupBusy: false })
        }
      },

      async flushPendingSaves() {
        const uid = authStore.user?.uid
        if (!uid) return
        this.update({ syncBusy: true, syncMsg: 'Flushing...' })
        try {
          await persistenceService.flushPending(uid)
          this.update({ syncMsg: 'Flush complete.' })
        } catch (e) {
          this.update({ syncMsg: 'Flush failed.' })
        }
        await this.refreshSyncCounts()
        this.update({ syncBusy: false })
      },

      async clearPendingQueue() {
        const uid = authStore.user?.uid
        if (!uid) return
        this.update({ syncBusy: true, syncMsg: 'Clearing queue...' })
        await persistenceService.clearPending(uid)
        this.update({ syncMsg: 'Queue cleared.' })
        await this.refreshSyncCounts()
        this.update({ syncBusy: false })
      },

      async clearBackups() {
        const uid = authStore.user?.uid
        if (!uid) return
        this.update({ syncBusy: true, syncMsg: 'Clearing backups...' })
        await persistenceService.clearConflictBackups(uid)
        this.update({ syncMsg: 'Backups cleared.' })
        await this.refreshSyncCounts()
        this.update({ syncBusy: false })
      },

      onReportEndpointInput(e) {
        this.update({ reportEndpoint: e.target.value, reportMsg: '' })
      },

      saveReportEndpoint() {
        const endpoint = (this.state.reportEndpoint || '').trim()
        localStorage.setItem(REPORT_ENDPOINT_KEY, endpoint)
        this.update({ reportMsg: endpoint ? 'Report endpoint saved.' : 'Report endpoint cleared.' })
      },

      async postReport(kind, payload) {
        const endpoint = (this.state.reportEndpoint || '').trim()
        if (!endpoint) throw new Error('No report endpoint configured.')

        const controller = new AbortController()
        const timeout = setTimeout(() => controller.abort(), 10_000)
        try {
          const res = await fetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              app: 'anyquest',
              kind,
              sentAt: Date.now(),
              payload,
            }),
            signal: controller.signal,
          })
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
        } finally {
          clearTimeout(timeout)
        }
      },

      queueReport(kind, payload, errorMsg) {
        const queue = loadReportQueue()
        queue.unshift({
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          ts: Date.now(),
          kind,
          payload,
          error: String(errorMsg || '').slice(0, 140),
        })
        saveReportQueue(queue)
        this.refreshReportQueueCount()
      },

      async sendPerfReport() {
        this.update({ reportBusy: true, reportMsg: '' })
        try {
          const entries = this.filteredPerfEntries(getAITelemetry())
          const snapshot = buildAITelemetrySnapshot(entries)
          await this.postReport('ai_perf', snapshot)
          this.update({ reportMsg: 'Perf report sent successfully.' })
        } catch (e) {
          const entries = this.filteredPerfEntries(getAITelemetry())
          const snapshot = buildAITelemetrySnapshot(entries)
          this.queueReport('ai_perf', snapshot, e)
          this.update({ reportMsg: 'Perf report failed; queued for retry.' })
        } finally {
          this.update({ reportBusy: false })
        }
      },

      async retryQueuedReports() {
        const queue = loadReportQueue()
        if (!queue.length) {
          this.update({ reportMsg: 'No queued reports.' })
          return
        }
        this.update({ reportBusy: true, reportMsg: '' })
        const remaining = []
        let sent = 0
        for (const item of queue) {
          try {
            await this.postReport(item.kind, item.payload)
            sent += 1
          } catch (e) {
            remaining.push({ ...item, error: String(e).slice(0, 140) })
          }
        }
        saveReportQueue(remaining)
        this.refreshReportQueueCount()
        this.update({
          reportBusy: false,
          reportMsg: remaining.length
            ? `Sent ${sent}; ${remaining.length} still queued.`
            : `Sent ${sent}; queue is clear.`,
        })
      },

      selectFeedback(rating) {
        this.update({ feedbackRating: rating })
      },

      onFeedbackInput(e) {
        this.update({ feedbackText: e.target.value, feedbackSavedMsg: '' })
      },

      async submitFeedback() {
        const message = this.state.feedbackText.trim()
        const rating = this.state.feedbackRating
        if (!message && !rating) return

        const fullMessage = rating ? `[rating:${rating}] ${message || '(rating only)'}` : message
        const result = await emailService.submitFeedback(fullMessage)
        this.update({
          feedbackText: result.ok ? '' : this.state.feedbackText,
          feedbackRating: result.ok ? null : this.state.feedbackRating,
          feedbackSavedMsg: result.message,
        })
      },

      triggerGalleryOpen() {
        this.update({ galleryOpenSeq: this.state.galleryOpenSeq + 1 })
      },

      async signOut() {
        await authStore.signOut()
      },

      openAiSettings() {
        playEffect('ui_open')
        dispatcher.trigger('sys-panel', TOPICS.ui.dialogOpen, { content: 'settings-dialog', data: { initialTab: 'advanced' } })
      },

      openHowToPlay() {
        playEffect('ui_open')
        dispatcher.trigger('sys-panel', TOPICS.ui.dialogOpen, { content: 'how-to-play-dialog', data: {}, options: { variant: 'notebook' } })
      },
    }
  </script>
</sys-panel>
